import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators } from 'redux';
import { notification, NOTIFICATION_TYPES } from '_atoms';
import history from 'helpers/history';
import { AbilityContext, ACTIONS, UI } from 'permission';
import * as actions from 'actions';
import { getUser } from 'store/selectors/authorizeSelectors';
import { clearCandidatesJob, setCandidateExists } from 'store/reducers/jobReducer';
import {
  getIsCandidateExistsOnJob,
  getJobActivityListLoading,
  getJobApproversLoading,
  getJobAssignmentId,
  getJobCandidateDataLoading,
  getJobEntryDateLoading,
  getJobLoader,
  getJobOfferingLoading,
} from 'store/selectors/jobSelectors';
import { JobStepsPanel } from '_molecules';
import { StepsLeftPanel, StepsRightPanel } from '_organisms';
import type { AppDispatch, RootState } from 'store';
import type {
  JobWorkflowStep,
  Interview as InterviewEntity,
  Opening as OpeningEntity,
} from 'store/reducers/jobReducer';
import type { Uuid } from 'store/reducers/types';
import './style.scss';

interface OwnProps {
  activeStep?: Uuid;
  steps: JobWorkflowStep[];
  userData: Record<string, unknown> & { uuid: Uuid };
  getCandidateStepAssignment: (
    jobId: Uuid,
    candidateId: Uuid,
    clear: boolean,
    steps: JobWorkflowStep[],
    step: number
  ) => Promise<void>;
  candidates: Record<string, unknown>;
  candidate?: Uuid;
  id: Uuid;
  getCandidatesJobs: (...args: unknown[]) => Promise<{ results: unknown[]; count: number } | null | undefined>;
  getSteps: (id: Uuid, updateOnlySteps?: boolean) => Promise<JobWorkflowStep[] | null | undefined>;
  approversCandidate: unknown[];
  author?: { uuid?: Uuid } | null;
  jobData: { status?: string; name?: string; service?: { uuid?: Uuid }; company?: { uuid?: Uuid } };
  openingsList: OpeningEntity[];
  offeringCandidate: Record<string, unknown>;
  probationPeriod?: unknown;
  getOpenings?: (body: unknown) => void;
  openingCandidate?: OpeningEntity | null;
  rooms?: unknown;
  updateData: (field: string, value: unknown) => void;
  getActivityList: (jobId: Uuid, candidateUuid: Uuid) => void;
  approveRR: (activeStep: number) => void;
  steps_loading?: unknown;
  activityList?: unknown;
  selectRR?: unknown;
  recruiter?: unknown;
  selectedRR?: unknown;
  cvs?: unknown;
  cvsLinks?: unknown;
  scorecard?: unknown;
}

interface State {
  activeStep: number;
  candidateSearch: string;
  onlyMyCandidates: boolean;
  statuses: Array<{ value: string; label: string }>;
}

function mapStateToProps(store: RootState) {
  return {
    loader: getJobLoader(store),
    approversCandidateLoading: getJobApproversLoading(store),
    activityListLoading: getJobActivityListLoading(store),
    offeringCandidateLoading: getJobOfferingLoading(store),
    user: getUser(store),
    candidateDataLoading: getJobCandidateDataLoading(store),
    isCandidateExistsOnJob: getIsCandidateExistsOnJob(store),
    entryDateLoading: getJobEntryDateLoading(store),
    jobAssignmentId: getJobAssignmentId(store),
  };
}

function mapDispatchToProps(dispatch: AppDispatch) {
  return {
    getApprovers: bindActionCreators(actions.getApprovers, dispatch) as unknown as (
      jobId: Uuid,
      stepId: Uuid,
      candidateId: Uuid
    ) => Promise<void>,
    actionWithdraw: bindActionCreators(actions.actionWithdraw, dispatch) as unknown as (
      jobId: Uuid,
      stepId: Uuid,
      candidateId: Uuid,
      body: Record<string, unknown>
    ) => Promise<void>,
    submitDecision: bindActionCreators(actions.submitDecision, dispatch) as unknown as (
      jobId: Uuid,
      stepId: Uuid,
      candidateId: Uuid,
      body: Record<string, unknown>,
      finalDecision?: boolean
    ) => Promise<unknown>,
    assignToStep: bindActionCreators(actions.assignToStep, dispatch) as unknown as (
      jobId: Uuid,
      stepId: Uuid,
      candidateId: Uuid,
      body: Record<string, unknown>
    ) => Promise<void>,
    createInterview: bindActionCreators(actions.createInterview, dispatch) as unknown as (
      jobId: Uuid,
      stepId: Uuid,
      candidateId: Uuid,
      body: actions.SaveInterviewPayload
    ) => Promise<InterviewEntity | null>,
    confirmInterview: bindActionCreators(actions.confirmInterview, dispatch) as unknown as (
      jobId: Uuid,
      stepId: Uuid,
      candidateId: Uuid,
      interviewId: Uuid
    ) => Promise<unknown>,
    updateCandidateResolution: bindActionCreators(actions.updateCandidateResolution, dispatch) as unknown as (
      jobId: Uuid,
      candidateId: Uuid,
      jobAssignment: Uuid,
      body: { opening_id: Uuid; [key: string]: unknown },
      steps: JobWorkflowStep[],
      step: number
    ) => void,
    submitDecisionWithOppening: bindActionCreators(actions.submitDecisionWithOppening, dispatch) as unknown as (
      jobId: Uuid,
      stepId: Uuid,
      candidateId: Uuid,
      body: Record<string, unknown>
    ) => Promise<void>,
    submitOffer: bindActionCreators(actions.submitOffer, dispatch) as unknown as (
      jobId: Uuid,
      stepId: Uuid,
      candidateId: Uuid,
      data: Record<string, unknown>
    ) => Promise<void>,
    deleteInterview: bindActionCreators(actions.deleteInterview, dispatch) as unknown as (
      jobId: Uuid,
      stepId: Uuid,
      candidateId: Uuid,
      interviewId: Uuid,
      body: Record<string, unknown>
    ) => Promise<unknown>,
    setCandidateExists: bindActionCreators(setCandidateExists, dispatch),
    clearCandidatesJob: () => dispatch(clearCandidatesJob()),
    getTodaysInterviews: () => dispatch(actions.getTodaysInterviewsAPI()),
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = OwnProps & PropsFromRedux;

class JobStepsTab extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const activeStep = props.activeStep
      ? (props.steps.find(v => v.uuid === props.activeStep)?.ordering_number as number)
      : 0;
    this.state = {
      activeStep,
      candidateSearch: '',
      onlyMyCandidates: false,
      statuses: (this.props.steps[activeStep].assignments_statuses as unknown as Array<{ name: string }>).map(
        ({ name }) => ({ value: name, label: name })
      ),
    };
    this.setActiveStep = this.setActiveStep.bind(this);
    this.actionWithdraw = this.actionWithdraw.bind(this);
    this.assignToStep = this.assignToStep.bind(this);
    this.submitDecision = this.submitDecision.bind(this);
    this.submitDecisionWithOppening = this.submitDecisionWithOppening.bind(this);
    this.createInterview = this.createInterview.bind(this);
    this.updateCandidateResolution = this.updateCandidateResolution.bind(this);
    this.submitOffer = this.submitOffer.bind(this);
    this.cancelInterview = this.cancelInterview.bind(this);
    this.refreshCandidateStep = this.refreshCandidateStep.bind(this);
    this.approveRR = this.approveRR.bind(this);
  }

  componentWillUnmount() {
    this.props.setCandidateExists(false);
  }

  setActiveStep(value: number, candidateId?: Uuid) {
    if (Object.values(this.props.loader).find(loading => loading) || value === this.state.activeStep) return;
    this.props.updateData('activeStep', value);
    const statuses = (this.props.steps[value].assignments_statuses as unknown as Array<{ name: string }>).map(
      ({ name }) => ({ value: name, label: name })
    );
    this.setState({
      activeStep: value,
      statuses,
      candidateSearch: '',
    });
    history.push(
      `/jobs/${this.props.id}/show/steps/${this.props.steps[value].uuid}${candidateId ? `/${candidateId}` : ''}`
    );
  }

  updateCandidateResolution(data: { opening_id: Uuid; [key: string]: unknown }) {
    this.props.updateCandidateResolution(
      this.props.id,
      this.props.candidate as Uuid,
      this.props.jobAssignmentId,
      data,
      this.props.steps,
      this.state.activeStep
    );
  }

  updateList(activeStep: number, candidate?: Uuid) {
    Promise.all(
      this.state.statuses.map(status =>
        this.props.getCandidatesJobs(this.props.id, this.props.steps[activeStep].uuid, true, {
          limit: 5,
          offset: 0,
          status: status.value,
          only_my_candidates: this.state.onlyMyCandidates,
          search: this.state.candidateSearch,
          user_uuid: this.props.candidate,
        })
      )
    ).then(resp => {
      const data = (resp as unknown as Array<{ results: Array<{ candidate: { uuid: Uuid } }> }>)
        .map(el => el.results)
        .flat();
      const candidateId = candidate || this.props.candidate || data[0]?.candidate.uuid;
      if (resp && window.location.pathname.includes('show/steps')) {
        this.props.setCandidateExists(true);
        history.push(
          `/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}${
            candidateId ? `/${candidateId}` : ''
          }`
        );
      }
    });
  }

  refreshCandidateStep(activeStep: number, candidateId = this.props.candidate) {
    if (!candidateId) return;

    this.props.getSteps(this.props.id, true);
    this.updateList(activeStep, candidateId);
    this.props.getCandidateStepAssignment(this.props.id, candidateId, false, this.props.steps, activeStep);
  }

  actionWithdraw(activeStep: number, comment: string) {
    this.props
      .actionWithdraw(this.props.id, this.props.steps[activeStep].uuid, this.props.userData.uuid, { comment })
      .then(() => {
        this.props.getSteps(this.props.id);
        this.setState({ candidateSearch: '' });
        history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}`);
        this.updateList(activeStep);
      });
  }

  assignToStep(activeStep: number, step: JobWorkflowStep & { assignments_statuses?: Array<{ name: string }> }) {
    const candidateId = this.props.candidate;
    this.props
      .assignToStep(this.props.id, this.props.steps[activeStep].uuid, this.props.userData.uuid, { step: step.uuid })
      .then(() => {
        history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}`);
        this.props.getSteps(this.props.id);
        const statuses = (step.assignments_statuses || []).map(({ name }) => ({ value: name, label: name }));
        this.setState({ activeStep: step.ordering_number as number, statuses }, () => {
          history.push(`/jobs/${this.props.id}/show/steps/${step.uuid}/${candidateId}`);
        });
      });
  }

  submitDecision(activeStep: number, finalDecision: boolean, body: { reason?: string; positive?: boolean }) {
    if (!body.reason) {
      delete body.reason;
    }
    this.props
      .submitDecision(this.props.id, this.props.steps[activeStep].uuid, this.props.userData.uuid, body, finalDecision)
      .then(() => {
        this.props.getSteps(this.props.id)?.then(() => {
          const candidateId = this.props.candidate;
          history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}`);
          if (body.positive) {
            const newStepId = this.props.steps[activeStep + 1].uuid;
            const statuses = (
              this.props.steps.find(({ uuid }) => uuid === newStepId)?.assignments_statuses as unknown as Array<{
                name: string;
              }>
            ).map(({ name }) => ({ value: name, label: name }));
            this.setState({ statuses, activeStep: activeStep + 1 });
            history.push(`/jobs/${this.props.id}/show/steps/${newStepId}${candidateId ? `/${candidateId}` : ''}`);
          } else {
            this.setState({ candidateSearch: '' });
            history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}`);
            this.updateList(activeStep);
          }
        });
      });
  }

  submitDecisionWithOppening(activeStep: number, data: { positive?: boolean }) {
    this.props
      .submitDecisionWithOppening(this.props.id, this.props.steps[activeStep].uuid, this.props.userData.uuid, data)
      .then(() => {
        this.props.setCandidateExists(false);
        this.props.getSteps(this.props.id)?.then(() => {
          if (data.positive) {
            if (activeStep === 6) {
              history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}`);
              this.updateList(activeStep);
            } else {
              const candidateId = this.props.candidate;
              const newStepId = this.props.steps[activeStep + 1].uuid;
              const statuses = (
                this.props.steps.find(({ uuid }) => uuid === newStepId)?.assignments_statuses as unknown as Array<{
                  name: string;
                }>
              ).map(({ name }) => ({ value: name, label: name }));
              this.setState({ statuses, activeStep: activeStep + 1 });
              history.push(`/jobs/${this.props.id}/show/steps/${newStepId}${candidateId ? `/${candidateId}` : ''}`);
            }
          } else {
            this.setState({ candidateSearch: '' });
            history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}`);
            this.updateList(activeStep);
          }
        });
      });
  }

  createInterview(activeStep: number, body: actions.SaveInterviewPayload) {
    const newInterview = !body.uuid;
    const candidateId = this.props.candidate;

    this.props
      .createInterview(this.props.id, this.props.steps[activeStep].uuid, this.props.userData.uuid, body)
      .then(interview => {
        if (!interview) return;

        this.props.getSteps(this.props.id, !newInterview)?.then(() => {
          if (newInterview) {
            notification({ type: NOTIFICATION_TYPES.SUCCESS, message: `Interview has been created` });
            const statuses = (
              this.props.steps.find(({ uuid }) => uuid === this.props.steps[activeStep].uuid)
                ?.assignments_statuses as unknown as Array<{ name: string }>
            ).map(({ name }) => ({ value: name, label: name }));
            this.setState({ statuses });
            history.push(
              `/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}${
                candidateId ? `/${candidateId}` : ''
              }`
            );
            this.updateList(activeStep);
            this.props.getCandidateStepAssignment(
              this.props.id,
              this.props.candidate as Uuid,
              false,
              this.props.steps,
              activeStep
            );
          } else {
            notification({ type: NOTIFICATION_TYPES.SUCCESS, message: `Interview has been updated` });
          }
          this.props.getActivityList(this.props.id, this.props.userData.uuid as Uuid);
          this.props.getTodaysInterviews();
        });
      })
      .catch(() => {});
  }

  confirmInterview(activeStep: number, interviewId: Uuid, closePopup: (value?: boolean) => void) {
    this.props
      .confirmInterview(this.props.id, this.props.steps[activeStep].uuid, this.props.userData.uuid, interviewId)
      .then(() => {
        notification({ type: NOTIFICATION_TYPES.SUCCESS, message: `Interview has been confirmed` });
        this.refreshCandidateStep(activeStep);
        closePopup();
      });
  }

  submitOffer(activeStep: number, data: Record<string, unknown>) {
    const candidateId = this.props.candidate;
    this.props
      .submitOffer(this.props.id, this.props.steps[activeStep].uuid, this.props.userData.uuid, data)
      .then(() => {
        history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}`);
        this.props.getSteps(this.props.id)?.then(() => {
          this.setState({ candidateSearch: '' });
          history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[activeStep].uuid}/${candidateId}`);
          this.updateList(activeStep);
        });
      });
  }

  cancelInterview(uuidToRemove: Uuid, data: unknown) {
    const candidateId = this.props.candidate;

    this.props
      .deleteInterview(
        this.props.id,
        this.props.steps[this.state.activeStep].uuid,
        this.props.userData.uuid,
        uuidToRemove,
        data as Record<string, unknown>
      )
      .then(() => {
        notification({ type: NOTIFICATION_TYPES.SUCCESS, message: `Interview has been canceled` });
        history.push(`/jobs/${this.props.id}/show/steps/${this.props.steps[this.state.activeStep].uuid}`);
        this.props.clearCandidatesJob();
        const statuses = (
          this.props.steps.find(({ uuid }) => uuid === this.props.steps[this.state.activeStep].uuid)
            ?.assignments_statuses as unknown as Array<{ name: string }>
        ).map(({ name }) => ({ value: name, label: name }));
        this.setState({ statuses });
        this.refreshCandidateStep(this.state.activeStep, candidateId);
        this.props.getTodaysInterviews();
      })
      .catch(() => {});
  }

  approveRR() {
    this.props.approveRR(this.state.activeStep);
  }

  render() {
    const stepData = this.props.steps.length ? this.props.steps[this.state.activeStep] : undefined;
    return (
      <div className="job-steps">
        <JobStepsPanel
          activeStep={this.state.activeStep}
          steps={this.props.steps as never}
          setActiveStep={this.setActiveStep}
        />
        <div className="job-steps_wrapperInfo">
          <StepsLeftPanel
            getCandidatesJobs={this.props.getCandidatesJobs as never}
            activeStepLabels={this.props.steps[this.state.activeStep].assignments_statuses as never}
            setActiveStep={stepId => {
              const activeStep = this.props.steps.findIndex(s => s.uuid === stepId);
              this.setActiveStep(activeStep, this.props.candidate);
            }}
            candidates={this.props.candidates as never}
            candidateSearch={this.state.candidateSearch}
            onlyMyCandidates={this.state.onlyMyCandidates}
            statuses={this.state.statuses}
            updateParams={(field, value) => this.setState({ [field]: value } as unknown as State)}
          />
          <StepsRightPanel
            {...this.props}
            step={stepData as never}
            updateCandidateResolution={this.updateCandidateResolution}
            hasAccessToActions={(this.context as React.ContextType<typeof AbilityContext>).can(
              ACTIONS.READ,
              UI.ACTIONS_TL_SA
            )}
            activeStep={this.state.activeStep}
            submitOffer={data => {
              this.submitOffer(this.state.activeStep, data);
            }}
            submitDecisionWithOppening={data => {
              this.submitDecisionWithOppening(this.state.activeStep, data as { positive?: boolean });
            }}
            submitDecision={(finalDecision: boolean, body: unknown) => {
              this.submitDecision(this.state.activeStep, finalDecision, body as { reason?: string });
            }}
            actionWithdraw={comment => {
              this.actionWithdraw(this.state.activeStep, comment);
            }}
            assignToStep={step => {
              this.assignToStep(this.state.activeStep, step as never);
            }}
            createInterview={body => {
              this.createInterview(this.state.activeStep, body);
            }}
            confirmInterview={(interviewId, closePopup) => {
              this.confirmInterview(this.state.activeStep, interviewId as Uuid, closePopup);
            }}
            refreshCandidateStep={() => this.refreshCandidateStep(this.state.activeStep)}
            userData={this.props.userData as never}
            approversCandidate={this.props.approversCandidate as never}
            empty={!this.props.candidate || !this.props.isCandidateExistsOnJob}
            loading={
              this.props.candidateDataLoading ||
              (this.props.isCandidateExistsOnJob && !Object.keys(this.props.userData).length)
            }
            cancelInterview={this.cancelInterview}
          />
        </div>
      </div>
    );
  }
}

JobStepsTab.contextType = AbilityContext;

export default connector(JobStepsTab);
